import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slider,
  Stack,
  Typography,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { isEqual } from "lodash";
import { Transition } from "../../pages/app/utils/animation";
import { useModals } from "../../store/dialogs/hooks";

const findPowerOfTwo = (value) => {
  if (value) {
    let power = 0;
    while (value > 1) {
      value = value >> 1;
      power++;
    }
    return power;
  }
  return null;
};

export const FineTuneDialog = ({
  fftSamples,
  maxFrequency = 20000,
  minDistance = 1000,
  onApply,
  onCancel,
  zoom = [0, 20000],
}) => {
  const { showFFTSettingsDialog } = useModals();
  const [zoomAmt, setZoomAmt] = useState(zoom);
  const [fftSize, setFftSize] = useState(findPowerOfTwo(fftSamples) || 9); // fftSize = 2^9 = 512, to have the labels on the slider be evenly spaced, the values have to be evenly spaced
  const hzBreakpoint = 1000;
  const textFieldWidth = "82px";

  const handleReset = () => {
    setZoomAmt(zoom);
    setFftSize(findPowerOfTwo(fftSamples) || 9);
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  const handleZoomChange = (_event, value, activeThumb) => {
    if (activeThumb === 0) {
      setZoomAmt([Math.min(value[0], zoomAmt[1] - minDistance), zoomAmt[1]]);
    } else {
      setZoomAmt([zoomAmt[0], Math.max(value[1], zoomAmt[0] + minDistance)]);
    }
  };

  const handleApply = () => {
    console.log("apply", zoomAmt, fftSize);
    onApply(zoomAmt, Math.pow(2, fftSize));
  };

  const initialValue = {
    fft: findPowerOfTwo(fftSamples) || 9,
    zoom: zoom,
  };

  const isUpdated = isEqual(initialValue, {
    fft: fftSize,
    zoom: zoomAmt,
  });

  return (
    <Dialog
      aria-hidden={!showFFTSettingsDialog}
      closeAfterTransition={false}
      disableRestoreFocus
      onClose={handleCancel}
      open={showFFTSettingsDialog}
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle>Fine Tune The Spectrogram</DialogTitle>
      <DialogContent dividers>
        <DialogContentText mb={3}>
          Regenerating the spectrogram can take up to a minute on large files.
        </DialogContentText>
        <Typography variant="overline">FFT Size [samples]</Typography>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={3}
        >
          <Box display="flex" flex={1}>
            <Slider
              value={fftSize}
              min={8}
              max={13}
              step={1}
              onChange={(_event, newValue) => setFftSize(newValue)}
              valueLabelDisplay="off"
            />
          </Box>
          <TextField
            label="Samples"
            value={Math.pow(2, fftSize)}
            variant="outlined"
            size="small"
            slotProps={{ input: { readOnly: true } }}
            sx={{ width: textFieldWidth }}
          />
        </Stack>
        <Box display="flex" mb={1} mt={1}>
          <Typography variant="overline">Frequency Range</Typography>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={3}
        >
          <TextField
            label={zoomAmt[0] < hzBreakpoint ? "Hz" : "kHz"}
            value={zoomAmt[0] < hzBreakpoint ? zoomAmt[0] : zoomAmt[0] / 1000}
            variant="outlined"
            size="small"
            slotProps={{ htmlInput: { readOnly: true } }}
            sx={{ width: textFieldWidth }}
          />
          <Box display="flex" flex={1}>
            <Slider
              value={zoomAmt}
              min={0}
              max={maxFrequency}
              step={100}
              onChange={handleZoomChange}
              valueLabelDisplay="off"
            />
          </Box>
          <TextField
            label={zoomAmt[1] < hzBreakpoint ? "Hz" : "kHz"}
            slotProps={{ htmlInput: { readOnly: true } }}
            value={zoomAmt[1] < hzBreakpoint ? zoomAmt[1] : zoomAmt[1] / 1000}
            variant="outlined"
            size="small"
            sx={{ width: textFieldWidth }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ mt: 1 }}>
        <Button variant="outlined" onClick={handleCancel} sx={{ mr: "auto" }}>
          Cancel Changes
        </Button>
        <Button disabled={isUpdated} variant="outlined" onClick={handleReset}>
          Reset
        </Button>
        <Button disabled={isUpdated} variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
